import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FlatList, NativeScrollEvent, NativeSyntheticEvent, Platform, Pressable, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import TableSkeleton from '../../../components/TableSkeleton';
import ExportButton from '../components/ExportButton';
import styles from '../OrderRulesReport.styles';

interface FixedColumn {
  key: string;
  label: string;
  minWidth: number;
  align?: 'left' | 'right' | 'center';
}

interface CustomerUtilRow {
  region: string;
  market: string;
  location: string;
  customerBp: string;
  customer: string;
  keyAccount: string;
  enrolledSkus: string | number;
  eligibleSkus: string | number;
  volumeEligibleSkus: string | number;
  volumeAllSkus: string | number;
  /** Pre-computed by the API; preferred over ratio when present. */
  customerPerformancePct?: number;
  // Legacy fields kept for backward compat with derived data path
  performanceValue?: number;
  performancePct?: string;
}

interface SkuUtilRow {
  location: string;
  materialNumber: string;
  packageName: string;
  description: string;
  enrolledCustomers: string | number;
  totalLargeFormat: string | number;
  volumeEligibleSkus: string | number;
  volumeAllSkus: string | number;
  /** Pre-computed by the API; preferred over ratio when present. */
  skuPerformancePct?: number;
  // Legacy fields kept for backward compat with derived data path
  performanceValue?: number;
  performancePct?: string;
}

interface CustomerSkuUtilizationTabProps {
  loading: boolean;
  customerRows: CustomerUtilRow[];
  skuRows: SkuUtilRow[];
  /** Values currently applied as quick filters on the main filter bar, keyed by dimension. */
  activeQuickFilters: Record<QuickFilterDimension, Set<string>>;
  /** Toggles a cell value on/off as a filter on the main filter bar and applies immediately. */
  onToggleQuickFilter: (dimension: QuickFilterDimension, value: string) => void;
  /** Server-side sort state — column key + direction currently applied via the API. */
  sort: { col: string | null; dir: 'asc' | 'desc' | null };
  /** Fires the sortBy/sortOrder API call for the underlying paginated dataset. */
  onSort: (colKey: string) => void;
  /** Pagination bar, rendered directly under the table. */
  pagination?: React.ReactNode;
  /** Fires the API call with `granularity` set to every selected Customer/Product column's backend value (in that group's order), or an empty array when none are selected. */
  onGranularityChange: (granularities: string[]) => void;
  /** Total matching groups reported by the API, used in the grouping note below the pagination bar. */
  totalElements: number;
  /** Triggers the export API for this tab. */
  onExport: () => Promise<void>;
}

export type QuickFilterDimension =
  | 'region'
  | 'market'
  | 'location'
  | 'customerBp'
  | 'customer'
  | 'keyAccount'
  | 'packageName'
  | 'materialNumber'
  | 'description';

type ColumnGroupId = 'geography' | 'customer' | 'product';

interface ColumnDef {
  key: string;
  label: string;
}

// Matches the HTML report's column selector: three dimension groups the user
// can pick any combination from. Geography columns always roll data up to
// that grain. Picking a Customer column pins the table to per-customer rows
// (hiding the SKU-side metrics); picking a Product column pins it to
// per-material rows (hiding the customer-side metrics). Since the underlying
// data here is two separately pre-aggregated arrays (no joined customer x SKU
// fact), Customer and Product selections are mutually exclusive.
const COLUMN_GROUPS: Array<{ id: ColumnGroupId; label: string; columns: ColumnDef[] }> = [
  {
    id: 'geography',
    label: 'Geography',
    columns: [
      { key: 'region', label: 'Region / Unit' },
      { key: 'market', label: 'Market / POD' },
      { key: 'location', label: 'Location' },
    ],
  },
  {
    id: 'customer',
    label: 'Customer',
    columns: [
      { key: 'customerBp', label: 'Customer BP' },
      { key: 'customer', label: 'Customer' },
      { key: 'keyAccount', label: 'Key Account' },
    ],
  },
  {
    id: 'product',
    label: 'Product',
    columns: [
      { key: 'packageName', label: 'Package' },
      { key: 'materialNumber', label: 'Material No.' },
      { key: 'description', label: 'Material Description' },
    ],
  },
];

const GEOGRAPHY_KEYS = ['region', 'market', 'location'];
const CUSTOMER_KEYS = ['customerBp', 'customer', 'keyAccount'];
const PRODUCT_KEYS = ['packageName', 'materialNumber', 'description'];

// Maps a column key to the backend's `granularity` value, sent (case-insensitive) whenever
// that column's checkbox is toggled. Geography columns have no granularity equivalent.
const COLUMN_TO_GRANULARITY: Partial<Record<string, string>> = {
  customerBp: 'customerId',
  customer: 'customerName',
  keyAccount: 'keyAccount',
  packageName: 'package',
  materialNumber: 'MaterialId',
  description: 'MaterialName',
};

const COLUMN_LABELS: Record<string, string> = COLUMN_GROUPS.reduce((acc, group) => {
  group.columns.forEach((column) => {
    acc[column.key] = column.label;
  });
  return acc;
}, {} as Record<string, string>);

/** minWidth for each dimension column key — ensures text is never clipped */
const DIMENSION_MIN_WIDTHS: Record<string, number> = {
  region: 150,
  market: 180,
  location: 200,
  customerBp: 140,
  customer: 200,
  keyAccount: 180,
  packageName: 150,
  materialNumber: 140,
  description: 240,
};

// Metric header tooltip text, matching the html report's info-tip copy for the Customer & SKU Utilization table.
const UTIL_METRIC_TOOLTIPS: Record<string, string> = {
  enrolledSkus: 'Count of enrolled SKU and customer combinations in the group.',
  eligibleSkus: 'Count of eligible SKU and customer combinations in the group, sourced from the Order Rule Product table for the sales location',
  customerPerf: 'Enrolled SKUs divided by Eligible SKUs, target 75 percent, rendered as a target-colored performance bar',
  enrolledCustomers: 'Distinct customers in the group with at least one enrolled SKU.',
  totalLargeFormat: 'Distinct eligible large format customers in the group.',
  skuPerf: 'Enrolled Customers divided by Total Large Format, target 75 percent, rendered as a target-colored performance bar',
  volumeEligibleSkus: 'cases sold in the last closed period across SKUs eligible for order rules',
  volumeAllSkus: 'Cases sold in the last closed period across every SKU, eligible or not. The difference against the eligible figure is the volume order rules cannot address.',
};

const toNumber = (value: string | number): number => Number(String(value).replace(/[^0-9.-]/g, '')) || 0;

const CustomerSkuUtilizationTab: React.FC<CustomerSkuUtilizationTabProps> = ({
  loading,
  customerRows,
  skuRows,
  activeQuickFilters,
  onToggleQuickFilter,
  sort: serverSort,
  onSort: handleServerSort,
  pagination,
  onGranularityChange,
  totalElements,
  onExport,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(() => new Set(GEOGRAPHY_KEYS));
  const [isColumnsDropdownVisible, setIsColumnsDropdownVisible] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<ColumnGroupId, boolean>>({
    geography: true,
    customer: true,
    product: true,
  });
  // Button position relative to the card so the dropdown scrolls with the page
  const [btnLayout, setBtnLayout] = useState({ top: 0 });
  const columnsBtnRef = useRef<any>(null);
  const cardRef = useRef<any>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [utilSort, setUtilSort] = useState<{ col: string | null; dir: 'asc' | 'desc' | null }>({ col: null, dir: null });

  const isRowHoverable = Platform.OS === 'web';

  const handleUtilSort = (colKey: string) => {
    setUtilSort((current) => {
      if (current.col !== colKey) return { col: colKey, dir: 'asc' };
      if (current.dir === 'asc') return { col: colKey, dir: 'desc' };
      if (current.dir === 'desc') return { col: null, dir: null };
      return { col: colKey, dir: 'asc' };
    });
    // Notify the parent so the paginated API call re-fetches with sortBy/sortOrder;
    // the grouped/aggregated rows above are still re-ordered locally since they
    // don't map 1:1 to a single backend column once grouped.
    handleServerSort(colKey);
  };

  const toggleGroupOpen = (groupId: ColumnGroupId) => {
    setOpenGroups((current) => ({ ...current, [groupId]: !current[groupId] }));
  };

  const ALL_COLUMN_KEYS = useMemo(
    () => COLUMN_GROUPS.flatMap((group) => group.columns.map((column) => column.key)),
    [],
  );

  const isAllSelected = selectedColumns.size === ALL_COLUMN_KEYS.length;
  const isNoneSelected = selectedColumns.size === 0;
  const isAllExpanded = COLUMN_GROUPS.every((group) => openGroups[group.id]);
  const isAllCollapsed = COLUMN_GROUPS.every((group) => !openGroups[group.id]);

  const selectAllColumns = () => {
    setSelectedColumns(new Set(ALL_COLUMN_KEYS));
    const activeGranularities = [...CUSTOMER_KEYS, ...PRODUCT_KEYS]
      .filter((columnKey) => COLUMN_TO_GRANULARITY[columnKey])
      .map((columnKey) => COLUMN_TO_GRANULARITY[columnKey] as string);
    onGranularityChange(activeGranularities);
  };

  const deselectAllColumns = () => {
    setSelectedColumns(new Set());
    onGranularityChange([]);
  };

  const expandAllGroups = () => {
    setOpenGroups({ geography: true, customer: true, product: true });
  };

  const collapseAllGroups = () => {
    setOpenGroups({ geography: false, customer: false, product: false });
  };

  const openColumnsDropdown = () => {
    const btnNode = columnsBtnRef.current;
    const cardNode = cardRef.current;
    if (btnNode && cardNode && typeof btnNode.measureLayout === 'function') {
      btnNode.measureLayout(
        cardNode,
        (_x: number, y: number, _w: number, h: number) => {
          setBtnLayout({ top: y + h + 6 });
          setIsColumnsDropdownVisible(true);
        },
        () => {
          setIsColumnsDropdownVisible(true);
        },
      );
    } else {
      setIsColumnsDropdownVisible(true);
    }
  };

  const toggleColumn = (groupId: ColumnGroupId, key: string) => {
    setSelectedColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      if (COLUMN_TO_GRANULARITY[key]) {
        // Send every currently-selected Customer/Product column's granularity value, not just the one just toggled.
        const activeGranularities = [...CUSTOMER_KEYS, ...PRODUCT_KEYS]
          .filter((columnKey) => next.has(columnKey))
          .map((columnKey) => COLUMN_TO_GRANULARITY[columnKey] as string);
        onGranularityChange(activeGranularities);
      }
      return next;
    });
  };

  const activeGeographyKeys = GEOGRAPHY_KEYS.filter((key) => selectedColumns.has(key));
  const activeCustomerKeys = CUSTOMER_KEYS.filter((key) => selectedColumns.has(key));
  const activeProductKeys = PRODUCT_KEYS.filter((key) => selectedColumns.has(key));
  const dimensionKeys = [...activeGeographyKeys, ...activeCustomerKeys, ...activeProductKeys];

  // Location -> Region/Market lookup, built from the customer rows (which carry full
  // geography), so the SKU rollup — which only has `location` — can still group by
  // Region or Market.
  const locationGeoMap = useMemo(() => {
    const map = new Map<string, { region: string; market: string }>();
    customerRows.forEach((row) => {
      if (row.location && !map.has(row.location)) {
        map.set(row.location, { region: row.region, market: row.market });
      }
    });
    return map;
  }, [customerRows]);

  const geoValueForSkuRow = (row: SkuUtilRow, key: string): string => {
    if (key === 'location') return row.location;
    const geo = locationGeoMap.get(row.location);
    return (key === 'region' ? geo?.region : geo?.market) || row.location;
  };

  interface DisplayRow {
    dims: Record<string, string>;
    enrolledSkus?: number;
    eligibleSkus?: number;
    enrolledCustomers?: number;
    totalLargeFormat?: number;
    /** Last-closed-period sales volume, accumulated separately per source array so the two never double count. */
    volumeEligibleSkusFromCustomer?: number;
    volumeAllSkusFromCustomer?: number;
    volumeEligibleSkusFromSku?: number;
    volumeAllSkusFromSku?: number;
    /** Pre-computed pct from API (used when exactly one source row maps to this display row, i.e. no aggregation needed) */
    apiCustomerPerformancePct?: number;
    apiSkuPerformancePct?: number;
    /** Number of source rows aggregated into this display row (used to decide whether pre-computed pct is still valid) */
    _customerSourceCount?: number;
    _skuSourceCount?: number;
  }

  // customerRows and skuRows are parallel arrays derived from the same API records (same
  // length, same order), so they're zipped by index — this also covers the case where the
  // user selects Customer *and* Product columns together, which previously produced an
  // empty table because neither family's loop ran.
  const displayRows = useMemo<DisplayRow[]>(() => {
    const groups = new Map<string, DisplayRow>();
    const rowCount = Math.min(customerRows.length, skuRows.length);

    for (let i = 0; i < rowCount; i++) {
      const custRow = customerRows[i];
      const skuRow = skuRows[i];

      const dims: Record<string, string> = {};
      activeGeographyKeys.forEach((key) => {
        dims[key] = String((custRow as any)?.[key] ?? geoValueForSkuRow(skuRow, key) ?? '');
      });
      activeCustomerKeys.forEach((key) => {
        dims[key] = String((custRow as any)?.[key] ?? '');
      });
      activeProductKeys.forEach((key) => {
        dims[key] = String((skuRow as any)?.[key] ?? '');
      });

      const groupKey = dimensionKeys.map((key) => dims[key] ?? '').join('||') || 'ALL';
      const existing =
        groups.get(groupKey) ??
        ({ dims, enrolledSkus: 0, eligibleSkus: 0, enrolledCustomers: 0, totalLargeFormat: 0, _customerSourceCount: 0, _skuSourceCount: 0 } as DisplayRow);

      existing.enrolledSkus = (existing.enrolledSkus ?? 0) + toNumber(custRow.enrolledSkus);
      existing.eligibleSkus = (existing.eligibleSkus ?? 0) + toNumber(custRow.eligibleSkus);
      existing.volumeEligibleSkusFromCustomer = (existing.volumeEligibleSkusFromCustomer ?? 0) + toNumber(custRow.volumeEligibleSkus);
      existing.volumeAllSkusFromCustomer = (existing.volumeAllSkusFromCustomer ?? 0) + toNumber(custRow.volumeAllSkus);
      existing._customerSourceCount = (existing._customerSourceCount ?? 0) + 1;
      // Only carry the pre-computed pct if exactly one source row rolls up to this group
      if (existing._customerSourceCount === 1 && custRow.customerPerformancePct != null) {
        existing.apiCustomerPerformancePct = custRow.customerPerformancePct;
      } else if (existing._customerSourceCount > 1) {
        existing.apiCustomerPerformancePct = undefined;
      }

      existing.enrolledCustomers = (existing.enrolledCustomers ?? 0) + toNumber(skuRow.enrolledCustomers);
      existing.totalLargeFormat = (existing.totalLargeFormat ?? 0) + toNumber(skuRow.totalLargeFormat);
      existing.volumeEligibleSkusFromSku = (existing.volumeEligibleSkusFromSku ?? 0) + toNumber(skuRow.volumeEligibleSkus);
      existing.volumeAllSkusFromSku = (existing.volumeAllSkusFromSku ?? 0) + toNumber(skuRow.volumeAllSkus);
      existing._skuSourceCount = (existing._skuSourceCount ?? 0) + 1;
      if (existing._skuSourceCount === 1 && skuRow.skuPerformancePct != null) {
        existing.apiSkuPerformancePct = skuRow.skuPerformancePct;
      } else if (existing._skuSourceCount > 1) {
        existing.apiSkuPerformancePct = undefined;
      }

      groups.set(groupKey, existing);
    }

    return Array.from(groups.values()).sort((a, b) => {
      for (const key of dimensionKeys) {
        const cmp = String(a.dims[key] ?? '').localeCompare(String(b.dims[key] ?? ''));
        if (cmp !== 0) return cmp;
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerRows, skuRows, activeGeographyKeys.join(','), activeCustomerKeys.join(','), activeProductKeys.join(',')]);

  // Customer metrics show unless Product-only is selected; SKU metrics show unless Customer-only is selected.
  // Both stay true by default (geography-only) and when Customer + Product are selected together (blended view).
  const showCustomerFamily = activeCustomerKeys.length > 0 || activeProductKeys.length === 0;
  const showSkuFamily = activeProductKeys.length > 0 || activeCustomerKeys.length === 0;

  const UTIL_NUMERIC_KEYS = new Set([
    'enrolledSkus', 'eligibleSkus', 'enrolledCustomers', 'totalLargeFormat',
    'volumeEligibleSkus', 'volumeAllSkus', 'customerPerf', 'skuPerf',
  ]);

  // Helper to get a comparable numeric value for computed metric columns
  const getUtilRowSortValue = (row: any, colKey: string): number | string => {
    if (colKey === 'customerPerf') {
      const enrolled = row.enrolledSkus ?? 0;
      const eligible = row.eligibleSkus ?? 0;
      return eligible > 0 ? (enrolled / eligible) * 100 : 0;
    }
    if (colKey === 'skuPerf') {
      const enrolled = row.enrolledCustomers ?? 0;
      const eligible = row.totalLargeFormat ?? 0;
      return eligible > 0 ? (enrolled / eligible) * 100 : 0;
    }
    if (colKey === 'volumeEligibleSkus') {
      return showCustomerFamily ? row.volumeEligibleSkusFromCustomer ?? 0 : row.volumeEligibleSkusFromSku ?? 0;
    }
    if (colKey === 'volumeAllSkus') {
      return showCustomerFamily ? row.volumeAllSkusFromCustomer ?? 0 : row.volumeAllSkusFromSku ?? 0;
    }
    if (UTIL_NUMERIC_KEYS.has(colKey)) return Number(row[colKey] ?? 0);
    // Dimension column
    return String(row.dims?.[colKey] ?? '');
  };

  const sortedDisplayRows = useMemo(() => {
    if (!utilSort.col || !utilSort.dir) return displayRows;
    const col = utilSort.col;
    const dir = utilSort.dir;
    return [...displayRows].sort((a, b) => {
      const aVal = getUtilRowSortValue(a, col);
      const bVal = getUtilRowSortValue(b, col);
      let cmp: number;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return dir === 'asc' ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRows, utilSort.col, utilSort.dir, showCustomerFamily]);

  // Fixed-width columns (like MaxPotentialTab) so headers never get trimmed.
  // No dimension columns (not even the 'All' fallback) when the user has deselected every column.
  const dimensionColumns: FixedColumn[] = dimensionKeys.map((key) => ({
    key,
    label: COLUMN_LABELS[key],
    minWidth: DIMENSION_MIN_WIDTHS[key] ?? 140,
    align: 'left' as const,
  }));

  const metricColumns: FixedColumn[] = [
    ...(showCustomerFamily
      ? [
          { key: 'enrolledSkus', label: 'Enrolled SKUs', minWidth: 130, align: 'center' as const },
          { key: 'eligibleSkus', label: 'Eligible SKUs', minWidth: 130, align: 'center' as const },
          { key: 'customerPerf', label: 'Customer Performance', minWidth: 210, align: 'center' as const },
        ]
      : []),
    ...(showSkuFamily
      ? [
          { key: 'enrolledCustomers', label: 'Enrolled Customers', minWidth: 155, align: 'center' as const },
          { key: 'totalLargeFormat', label: 'Total Large Format', minWidth: 155, align: 'center' as const },
          { key: 'skuPerf', label: 'SKU Performance', minWidth: 200, align: 'center' as const },
        ]
      : []),
    // Always shown regardless of which columns are selected, matching the html report.
    { key: 'volumeEligibleSkus', label: 'Last Period Sales Volume, Eligible SKUs', minWidth: 215, align: 'center' as const },
    { key: 'volumeAllSkus', label: 'Last Period Sales Volume, All SKUs', minWidth: 200, align: 'center' as const },
  ];

  const columns: FixedColumn[] = [...dimensionColumns, ...metricColumns];
  const selectedColumnCount = dimensionKeys.length;

  // Grouping note shown below the pagination bar, mirroring the html report's per-grouping caveat copy.
  const hasCustomerCols = activeCustomerKeys.length > 0;
  const hasProductCols = activeProductKeys.length > 0;
  const recordCount = sortedDisplayRows.length;
  const groupingNote =
    hasCustomerCols && hasProductCols
      ? `Showing the first ${recordCount} of ${totalElements} groups. Narrow the filters or remove a column to see fewer, larger groups. \u00b7 One customer and one SKU per row, so the count metrics do not apply. Status and volume are shown instead.`
      : hasProductCols
      ? `Showing the first ${recordCount} of ${totalElements} groups. Narrow the filters or remove a column to see fewer, larger groups. \u00b7 Grouped by material, so the SKU count metrics do not apply and are hidden.`
      : hasCustomerCols
      ? `Showing the first ${recordCount} of ${totalElements} groups. \u00b7 Grouped by customer, so the customer count metrics do not apply and are hidden.`
      : null;

  // ── Table scroll constants ─────────────────────────────────────────────────
  const UTIL_ROW_HEIGHT = 44;
  const UTIL_MAX_VISIBLE_ROWS = 12;
  // Extra bottom padding for the horizontal scrollbar so it never overlaps row text
  const HORIZONTAL_SCROLLBAR_PADDING = 10;

  // ── Scroll sync refs (header ↔ body, horizontal) ──────────────────────────
  const utilHeaderScrollRef = useRef<ScrollView>(null);
  const utilBodyScrollRef = useRef<ScrollView>(null);
  const isUtilHeaderScrolling = useRef(false);
  const isUtilBodyScrolling = useRef(false);

  const onUtilHeaderScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isUtilBodyScrolling.current) return;
    isUtilHeaderScrolling.current = true;
    utilBodyScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isUtilHeaderScrolling.current = false;
  }, []);

  const onUtilBodyScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isUtilHeaderScrolling.current) return;
    isUtilBodyScrolling.current = true;
    utilHeaderScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isUtilBodyScrolling.current = false;
  }, []);

  // Total pixel width of the util table — recomputed whenever columns change
  const utilContentWidthStyle = useMemo(
    () => ({ width: columns.reduce((acc, col) => acc + col.minWidth, 0) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns.map((c) => c.key).join(',')],
  );

  // Fixed-height body — shrink to fit for few rows, cap at UTIL_MAX_VISIBLE_ROWS,
  // plus extra padding for the horizontal scrollbar so single/few rows don't overlap with the bar
  const utilBodyHeight = useMemo(
    () => Math.min(sortedDisplayRows.length, UTIL_MAX_VISIBLE_ROWS) * UTIL_ROW_HEIGHT + HORIZONTAL_SCROLLBAR_PADDING,
    [sortedDisplayRows.length],
  );

  const getUtilItemLayout = useCallback(
    (_: any, index: number) => ({ length: UTIL_ROW_HEIGHT, offset: UTIL_ROW_HEIGHT * index, index }),
    [],
  );

  /**
   * Renders a horizontal progress bar for a performance percentage.
   *
   * @param enrolled  - numerator (enrolled SKUs or customers)
   * @param eligible  - denominator (eligible SKUs or total large format)
   * @param precomputed - optional percentage already calculated by the API;
   *                      used directly when the display row maps 1:1 to an API record.
   *
   * Target threshold is 75 %:
   *   - at or above → blue bar (#005CBC)
   *   - below       → red bar  (#C12C01)
   */
  const renderPerfCell = (enrolled: number, eligible: number, precomputed?: number) => {
    // 0 of 0 is not the same as 0 of many — a group with no eligible denominator has no performance to report.
    if (eligible <= 0) {
      return <Text style={styles.tableDataText}>Not eligible</Text>;
    }
    const value =
      precomputed != null
        ? Math.round(precomputed)
        : eligible > 0
        ? Math.round((enrolled / eligible) * 100)
        : 0;
    const clamped = Math.max(0, Math.min(100, value));
    const isAboveTarget = value >= 75;
    return (
      <View style={styles.customerPerfCell}>
        <View style={styles.customerPerfTrack}>
          <View
            style={[
              styles.customerPerfFill,
              { width: `${clamped}%`, backgroundColor: isAboveTarget ? '#005CBC' : '#C12C01' },
            ]}
          />
        </View>
        <Text style={[styles.customerPerfValue, isAboveTarget ? styles.customerPerfValueAboveTarget : styles.customerPerfValueBelowTarget]}>
          {`${value}%`}
        </Text>
      </View>
    );
  };

  // ── Render callback: utilization row (memoized for FlatList) ──────────────
  const renderUtilRow = useCallback(
    ({ item: row, index: rowIndex }: { item: typeof sortedDisplayRows[0]; index: number }) => {
      const isHovered = hoveredRowIndex === rowIndex;
      return (
        <Pressable
          style={[styles.tableDataRow, isHovered && styles.tableDataRowHover]}
          onHoverIn={isRowHoverable ? () => setHoveredRowIndex(rowIndex) : undefined}
          onHoverOut={isRowHoverable ? () => setHoveredRowIndex(null) : undefined}
        >
          {columns.map((column) => {
            const isDimensionColumn = dimensionKeys.includes(column.key) || column.key === 'all';
            return (
              <View
                key={column.key}
                style={[
                  styles.tableDataCell,
                  { width: column.minWidth, minWidth: column.minWidth, maxWidth: column.minWidth, overflow: 'hidden' },
                  column.align === 'right' && styles.alignRight,
                  column.align === 'center' && styles.alignCenter,
                ]}
              >
                {isDimensionColumn ? (
                  column.key === 'all' ? (
                    <Text style={[styles.tableDataText, styles.tableDataTextStrong]} numberOfLines={1}>All Locations</Text>
                  ) : (
                    (() => {
                      const dimension = column.key as QuickFilterDimension;
                      const value = row.dims[dimension] ?? '';
                      const isActive = !!value && (activeQuickFilters[dimension]?.has(value) ?? false);
                      return (
                        <TouchableOpacity
                          style={[styles.utilizationQuickFilterChip, isActive && styles.utilizationQuickFilterChipActive]}
                          onPress={() => (value ? onToggleQuickFilter(dimension, value) : undefined)}
                          activeOpacity={0.75}
                          disabled={!value}
                        >
                          <Text style={[styles.utilizationQuickFilterText, isActive && styles.utilizationQuickFilterTextActive]} numberOfLines={1}>
                            {value || '-'}
                          </Text>
                          {isActive ? <MaterialIcons name="close" size={13} color="#005CBC" /> : null}
                        </TouchableOpacity>
                      );
                    })()
                  )
                ) : column.key === 'enrolledSkus' ? (
                  <Text style={styles.tableDataText}>{(row.enrolledSkus ?? 0).toLocaleString('en-US')}</Text>
                ) : column.key === 'eligibleSkus' ? (
                  <Text style={styles.tableDataText}>{(row.eligibleSkus ?? 0).toLocaleString('en-US')}</Text>
                ) : column.key === 'customerPerf' ? (
                  renderPerfCell(row.enrolledSkus ?? 0, row.eligibleSkus ?? 0, row.apiCustomerPerformancePct)
                ) : column.key === 'enrolledCustomers' ? (
                  <Text style={styles.tableDataText}>{(row.enrolledCustomers ?? 0).toLocaleString('en-US')}</Text>
                ) : column.key === 'totalLargeFormat' ? (
                  <Text style={styles.tableDataText}>{(row.totalLargeFormat ?? 0).toLocaleString('en-US')}</Text>
                ) : column.key === 'skuPerf' ? (
                  renderPerfCell(row.enrolledCustomers ?? 0, row.totalLargeFormat ?? 0, row.apiSkuPerformancePct)
                ) : column.key === 'volumeEligibleSkus' ? (
                  <Text style={styles.tableDataText}>
                    {(showCustomerFamily ? row.volumeEligibleSkusFromCustomer ?? 0 : row.volumeEligibleSkusFromSku ?? 0).toLocaleString('en-US')}
                  </Text>
                ) : column.key === 'volumeAllSkus' ? (
                  <Text style={styles.tableDataText}>
                    {(showCustomerFamily ? row.volumeAllSkusFromCustomer ?? 0 : row.volumeAllSkusFromSku ?? 0).toLocaleString('en-US')}
                  </Text>
                ) : null}
              </View>
            );
          })}
        </Pressable>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hoveredRowIndex, isRowHoverable, columns, dimensionKeys, activeQuickFilters, onToggleQuickFilter, showCustomerFamily],
  );

  return (
    <View ref={cardRef} style={[styles.card, { overflow: 'visible' }]}>
      <View style={styles.cardHeaderRow}>
        <View>
          <Text style={styles.cardTitle}>Customer &amp; SKU Utilization</Text>
          <Text style={styles.cardSubTitle}>
            Current enrollment across enrolled SKUs and customers · choose the dimensions the table groups by
          </Text>
        </View>
        <View style={styles.utilizationHeaderActions}>
          <TouchableOpacity
            ref={columnsBtnRef}
            style={styles.utilizationColumnsBtn}
            onPress={openColumnsDropdown}
            activeOpacity={0.8}
            testID="order-rules-utilization-columns-toggle"
          >
            <MaterialIcons name="view-column" size={14} color="#4B5563" />
            <Text style={styles.utilizationColumnsBtnText}>{`Columns (${selectedColumnCount})`}</Text>
          </TouchableOpacity>
          <ExportButton testID="order-rules-export-utilization" onExport={onExport} />
        </View>
      </View>

      {/* Inline dropdown — anchored relative to the card, scrolls with the page */}
      {isColumnsDropdownVisible ? (
        <>
          {/* Full-screen invisible backdrop to dismiss on outside tap */}
          <Pressable
            style={styles.utilizationColsInlineBackdrop}
            onPress={() => setIsColumnsDropdownVisible(false)}
          />
          <View
            style={[
              styles.utilizationColsPopover,
              { position: 'absolute', top: btnLayout.top, right: 0, zIndex: 999 },
            ]}
          >
            <Text style={styles.utilizationColsPopoverTitle}>Table columns</Text>
            <Text style={styles.utilizationColumnsModalSubtitle}>
              Choose the dimensions the table groups by. The utilization metrics always display.
            </Text>
            <View style={styles.utilizationColsActionsRow}>
              <View style={styles.utilizationColsActionsSide}>
                <TouchableOpacity onPress={selectAllColumns} disabled={isAllSelected} activeOpacity={0.7}>
                  <Text style={[styles.utilizationColsActionBtn, isAllSelected && styles.utilizationColsActionBtnDisabled]}>Select All</Text>
                </TouchableOpacity>
                <View style={styles.utilizationColsActionDivider} />
                <TouchableOpacity onPress={deselectAllColumns} disabled={isNoneSelected} activeOpacity={0.7}>
                  <Text style={[styles.utilizationColsActionBtn, isNoneSelected && styles.utilizationColsActionBtnDisabled]}>Deselect All</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.utilizationColsActionsSide}>
                <TouchableOpacity onPress={expandAllGroups} disabled={isAllExpanded} activeOpacity={0.7}>
                  <Text style={[styles.utilizationColsActionBtn, isAllExpanded && styles.utilizationColsActionBtnDisabled]}>Expand All</Text>
                </TouchableOpacity>
                <View style={styles.utilizationColsActionDivider} />
                <TouchableOpacity onPress={collapseAllGroups} disabled={isAllCollapsed} activeOpacity={0.7}>
                  <Text style={[styles.utilizationColsActionBtn, isAllCollapsed && styles.utilizationColsActionBtnDisabled]}>Collapse All</Text>
                </TouchableOpacity>
              </View>
            </View>
            {COLUMN_GROUPS.map((group) => {
              const isOpen = openGroups[group.id];
              return (
                <View key={group.id} style={styles.utilizationGroupCard}>
                  <TouchableOpacity
                    style={styles.utilizationGroupCardHeader}
                    onPress={() => toggleGroupOpen(group.id)}
                    activeOpacity={0.8}
                  >
                    <Text style={styles.utilizationGroupCardTitle}>{group.label.toUpperCase()}</Text>
                    <MaterialIcons name={isOpen ? 'expand-less' : 'chevron-right'} size={20} color="#005CBC" />
                  </TouchableOpacity>
                  {isOpen ? (
                    <View style={styles.utilizationGroupCardBody}>
                      {group.columns.map((column) => {
                        const isChecked = selectedColumns.has(column.key);
                        return (
                          <TouchableOpacity
                            key={column.key}
                            style={styles.utilizationColumnOption2}
                            onPress={() => toggleColumn(group.id, column.key)}
                            activeOpacity={0.75}
                          >
                            <MaterialIcons
                              name={isChecked ? 'check-box' : 'check-box-outline-blank'}
                              size={18}
                              color={isChecked ? '#005CBC' : '#9CA3AF'}
                            />
                            <Text style={styles.utilizationColumnOptionText} numberOfLines={1}>{column.label}</Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  ) : null}
                </View>
              );
            })}
          </View>
        </>
      ) : null}

      {loading ? (
        <TableSkeleton rows={10} />
      ) : !displayRows || displayRows.length === 0 ? (
        <View style={styles.emptyStateWrap}>
          <Text style={styles.emptyStateText}>No utilization data for the current selection</Text>
        </View>
      ) : (
          <View>
            {/* ── Sticky column header — always visible ── */}
            <ScrollView
              ref={utilHeaderScrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              scrollEventThrottle={16}
              onScroll={onUtilHeaderScroll}
              bounces={false}
              contentContainerStyle={utilContentWidthStyle}
            >
              <View style={styles.tableHeaderRow}>
                {columns.map((column) => {
                  const isActive = utilSort.col === column.key;
                  return (
                    <TouchableOpacity
                      key={column.key}
                      style={[
                        styles.tableHeaderCell,
                        { width: column.minWidth, minWidth: column.minWidth, maxWidth: column.minWidth },
                        column.align === 'right' && styles.tableHeaderCellAlignRight,
                        column.align === 'center' && styles.tableHeaderCellAlignCenter,
                      ]}
                      onPress={() => handleUtilSort(column.key)}
                      activeOpacity={0.7}
                      accessibilityLabel={`Sort by ${column.label}`}
                    >
                      <View style={styles.maxPotentialHeaderTextWrap}>
                        <Text style={styles.tableHeaderText} numberOfLines={2}>{column.label}</Text>
                        {UTIL_METRIC_TOOLTIPS[column.key] ? (
                          <DLOTooltip content={UTIL_METRIC_TOOLTIPS[column.key]} trigger="hover" position="top" maxWidth={300}>
                            <View style={styles.maxPotentialHeaderInfoIcon}>
                              <MaterialIcons name="info-outline" size={12} color="#818181" />
                            </View>
                          </DLOTooltip>
                        ) : null}
                      </View>
                      <MaterialIcons
                        name={isActive ? (utilSort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                        size={14}
                        color={isActive ? '#005CBC' : '#9E9E9E'}
                        style={styles.tableHeaderSortIcon}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ScrollView>

            {/* ── Fixed-height body — rows scroll inside, not the page ── */}
            <View style={{ height: utilBodyHeight, overflow: 'hidden' }}>
              <ScrollView
                ref={utilBodyScrollRef}
                horizontal
                showsHorizontalScrollIndicator
                scrollEventThrottle={16}
                onScroll={onUtilBodyScroll}
                bounces={false}
                contentContainerStyle={utilContentWidthStyle}
              >
                <FlatList
                  data={sortedDisplayRows}
                  renderItem={renderUtilRow}
                  keyExtractor={(_, index) => `util-row-${index}`}
                  getItemLayout={getUtilItemLayout}
                  showsVerticalScrollIndicator
                  removeClippedSubviews
                  maxToRenderPerBatch={20}
                  windowSize={5}
                  initialNumToRender={15}
                />
              </ScrollView>
            </View>
          </View>
      )}
      {!loading && displayRows && displayRows.length > 0 ? pagination : null}
      {!loading && displayRows && displayRows.length > 0 && groupingNote ? (
        <Text style={styles.utilizationGroupingNote}>{groupingNote}</Text>
      ) : null}
    </View>
  );
};

export default CustomerSkuUtilizationTab;

